import '@ui/styles/sass/global/components/home.scss';
import React, { useState, useMemo, useCallback, Suspense } from 'react';
import UserProfile from '../../partials/layout/UserProfile';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import NotFoundRedirect from '@core/helpers/NotFoundRedirect';
import { EmbedError, ModuleErrorBoundary, ModuleLoader } from '@ui/components';
import { Button } from '@mui/material';
import { modules } from '../../router/moduleRegistry';
import { buildMenu } from './menu';
import enabledPages from '@core/constants/pages/enabledPages';
import { useTranslation } from 'react-i18next';
import { getDefaultFallbackPath } from '@core/helpers/PermissionsChecker';
import ProtectedRoute from '@core/components/route/ProtectedRoute';
import CustomPopper from '@ui/components/Popper';
import { ROUTES } from '@core/constants/routes/routes';
import Dashboards from '../../pages/dashboards/Dashboards';

export const Sidebar = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const currentPath = location.pathname;
    const defaultFallbackPath = getDefaultFallbackPath();

    const anchorRefs = React.useRef({});
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
    const [submenuItems, setSubmenuItems] = useState([]);
    const [popperOpen, setPopperOpen] = useState(false);

    const handleRoute = useCallback((path) => {
        history.push(path);
    }, [history]);

    // The menu is derived from the module registry plus the shell's own
    // entries. Feature flags and permissions are already applied.
    //
    // Memoised because hovering the group menu updates state and re-renders
    // this component: without it every hover would rebuild the icon elements
    // and re-run `checkPermissions`, which reads and parses the session from
    // localStorage once per entry. Permissions only change on login or logout,
    // and both navigate, so `currentPath` is enough to keep the menu fresh.
    const menuItems = useMemo(
        () => buildMenu({ t, currentPath, onNavigate: handleRoute }),
        [t, currentPath, handleRoute]
    );

    const openSubmenu = (item) => {
        setSubmenuAnchorEl(anchorRefs.current[item.id]);
        setSubmenuItems(item.submenu);
        setPopperOpen(true);
    };

    const closeSubmenu = () => {
        setPopperOpen(false);
        setSubmenuAnchorEl(null);
    };

    return (
        <div className='pages'>
            <div className='menu'>
                <UserProfile history={history} t={t} />
                {menuItems.map(item => {
                    const buttonClass = item.active
                        ? (item.activeClass || 'menu_button--active')
                        : 'menu_button';

                    // A group does not navigate: hovering it opens the submenu.
                    if (item.hasSubmenu) {
                        return (
                            <div
                                className='menu_component'
                                key={item.id}
                                ref={el => (anchorRefs.current[item.id] = el)}
                                onMouseEnter={() => openSubmenu(item)}
                                onMouseLeave={closeSubmenu}
                            >
                                <Button className={buttonClass}>
                                    <div className='menu_icon'>{item.icon}</div>
                                </Button>
                                <div className='menu_text'>{item.label}</div>
                            </div>
                        );
                    }

                    return (
                        <div className='menu_component' key={item.id}>
                            <Button
                                className={buttonClass}
                                onClick={item.onClick}
                            >
                                <div className='menu_icon'>{item.active ? item.iconHover : item.icon}</div>
                            </Button>
                            <div className='menu_text'>{item.label}</div>
                        </div>);
                })}

                {/* Submenu of whichever group is being hovered */}
                <CustomPopper
                    state={{ popperOpen, setPopperOpen }}
                    items={submenuItems}
                    anchorElement={submenuAnchorEl}
                />
            </div>
            <ModuleErrorBoundary
                fallback={
                    <EmbedError
                        onRetry={() => window.location.reload()}
                        message={t('errors.moduleLoad')}
                    />
                }
            >
                <Suspense fallback={<ModuleLoader />}>
                    <Switch>

                        {/* Redirect from root URL to /clients. */}
                        <Redirect exact from={ROUTES.HOME} to={defaultFallbackPath} />
                        <Redirect exact from="/cards" to={ROUTES.PRODUCTS.CARDS.BASE} />

                        <ProtectedRoute
                            exact
                            path={ROUTES.DASHBOARD}
                            component={Dashboards}
                            isPageEnabled={enabledPages.enableDashboards}
                            action='show_dashboards.main'
                        />

                        {modules.map(module => (
                            <Route
                                key={module.id}
                                path={module.basePath}
                                component={module.load}
                            />
                        ))}
                        <Route
                            exact
                            path='*'
                            component={NotFoundRedirect}
                        />
                    </Switch>
                </Suspense>
            </ModuleErrorBoundary>
        </div>
    );
};

export default Sidebar;
